import * as React from 'react';
import { observer } from 'mobx-react';
import { runInAction } from 'mobx';
import * as uuid from 'uuid';
import InputWrapper, { InputType } from '../Inputs/InputWrapper';
import { BoundProp, getBoundValue, setBoundValue } from '../Inputs/BoundModelProperty';

export interface ISimpleTimePickerProps<T> {
	model: T;
	modelProperty: BoundProp<T, string>;
	label?: string;
	placeholder?: string;
	isDisabled?: boolean;
	isReadOnly?: boolean;
	isRequired?: boolean;
	errors?: string | string[];
	className?: string;
	onAfterChange?: () => void;
	minuteStep?: number;
}

interface ParsedTime {
	hour12: number;
	minute: number;
	period: 'AM' | 'PM';
}

function parseTimeString(timeStr: string | undefined | null): ParsedTime | null {
	if (!timeStr || typeof timeStr !== 'string') return null;
	const parts = timeStr.split(':');
	if (parts.length < 2) return null;

	const hour24 = parseInt(parts[0], 10);
	const minute = parseInt(parts[1], 10);

	if (Number.isNaN(hour24) || Number.isNaN(minute)) return null;

	const period: 'AM' | 'PM' = hour24 >= 12 ? 'PM' : 'AM';
	let hour12 = hour24 % 12;
	if (hour12 === 0) hour12 = 12;

	return { hour12, minute, period };
}

function toTimeString(hour12: number, minute: number, period: 'AM' | 'PM'): string {
	let hour24 = hour12 % 12;
	if (period === 'PM') hour24 += 12;
	return `${hour24.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}:00`;
}

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = Array.from({ length: 60 }, (_, i) => i);

export const SimpleTimePicker = observer(<T,>(props: ISimpleTimePickerProps<T>) => {
	const { model, modelProperty, label, isDisabled, isReadOnly, isRequired, errors, className, onAfterChange } = props;

	const id = React.useMemo(() => uuid.v4(), []);
	const fieldId = `${id}-field`;

	const currentValue = getBoundValue(model, modelProperty);
	const parsed = parseTimeString(currentValue);

	const hour = parsed?.hour12 ?? 12;
	const minute = parsed?.minute ?? 0;
	const period = parsed?.period ?? 'AM';

	const updateModel = (newHour: number, newMinute: number, newPeriod: 'AM' | 'PM') => {
		const timeStr = toTimeString(newHour, newMinute, newPeriod);
		runInAction(() => {
			setBoundValue(model, modelProperty, timeStr);
		});
		onAfterChange?.();
	};

	const disabled = isDisabled || isReadOnly;

	return (
		<InputWrapper
			inputType={InputType.TIME}
			id={id}
			inputId={fieldId}
			className={className}
			isRequired={isRequired}
			label={label}
			errors={errors}
		>
			<div className="simple-time-picker">
				<select
					id={fieldId}
					className="simple-time-picker__select simple-time-picker__hour"
					value={hour}
					disabled={disabled}
					onChange={e => updateModel(parseInt(e.target.value, 10), minute, period)}
				>
					{HOURS.map(h => (
						<option key={h} value={h}>
							{h}
						</option>
					))}
				</select>
				<span className="simple-time-picker__separator">:</span>
				<select
					className="simple-time-picker__select simple-time-picker__minute"
					value={minute}
					disabled={disabled}
					onChange={e => updateModel(hour, parseInt(e.target.value, 10), period)}
				>
					{MINUTES.map(m => (
						<option key={m} value={m}>
							{m.toString().padStart(2, '0')}
						</option>
					))}
				</select>
				<select
					className="simple-time-picker__select simple-time-picker__period"
					value={period}
					disabled={disabled}
					onChange={e => updateModel(hour, minute, e.target.value as 'AM' | 'PM')}
				>
					<option value="AM">AM</option>
					<option value="PM">PM</option>
				</select>
			</div>
		</InputWrapper>
	);
});
